/**
 * ประลองยุทธภพ (the weekly tournament, C0-TOURNEY; docs/design/ideas/2026-10-11-weekly-tournament.md, decision in
 * docs/DECISIONS_2026-10-11.md). Not ประลองยุทธ (the daily arena, data/arena.ts): that one stays as it is.
 *
 * The offline version: the player, 24 wanderers (data/wanderers.ts) and the 7 sparring folk (data/sparring.ts) make 32
 * entrants. Swiss rounds on Saturday 20:00 (the in-game clock), the top four in a knock-out on Sunday 20:00. The
 * player's bouts are fought for real (systems/duelMatch.ts, best of three at the ring, fair-arena rules); the others are
 * decided by the event's own seeded draws. The champion holds the golden dragon palm (data/legendary.ts) for the week.
 *
 * Data only; the rules are `systems/tournament.ts`, the pairing is `swiss.ts`.
 */
import { DRAGON_PALM_ART } from './dragonPalm';

export const TOURNEY_ART = DRAGON_PALM_ART;

/** The two sizes of event. */
export interface TourneyMode {
  readonly entrants: number;
  /** Swiss rounds (online: `swissRounds(n)`; 32 gives 5, 16 gives 4). */
  readonly rounds: number;
  /** Players who go to the knock-out (a power of two). */
  readonly cut: number;
}
export const TOURNEY_MODES = {
  week: { entrants: 32, rounds: 5, cut: 4 },
  /** The short test event (the herald's test field): starts at once, awards no palm and no hall entry. */
  test: { entrants: 16, rounds: 4, cut: 4 },
} as const satisfies Record<string, TourneyMode>;
export type TourneyModeId = keyof typeof TOURNEY_MODES;

export const TOURNEY = {
  /** Days of the week are counted from the in-game day count: 0 is Monday. */
  signupFrom: { day: 0, hour: 0 },
  /** Sign-up closes on Saturday at 19:30. */
  signupTo: { day: 5, hour: 19.5 },
  /** The Swiss rounds start on Saturday at 20:00, the knock-out on Sunday at 20:00. */
  swissAt: { day: 5, hour: 20 },
  cutAt: { day: 6, hour: 20 },
  /** Weeks won in a row that make the holder a guest grandmaster, who then sits out one week. */
  streakForGuest: 3,
  /** Bouts of the player's that decide nothing (an exhibition after a bye) pay this many coins. */
  exhibitionCoins: 2,
  /** The most hall entries kept. */
  hallMax: 52,
} as const;

/**
 * The fair arena: in a tournament bout both fighters have the same numbers. Gear counts for nothing (systems/gearCombat.ts),
 * every move is at one level (World.skillLevel), and the base stats below are held each tick (systems/duelMatch.ts). What
 * is left to decide a bout is the art, the reading and the timing. A style's own weight (data/duelMatch.ts DUEL_BUILDS) still
 * evens the arts out for the rival, as it does in any bout.
 */
export const FAIR_ARENA = {
  skillLevel: 6,
  neigongLevel: 12,
  atk: 25,
  magic: 25,
  def: 17,
  magicDef: 17,
  crit: 0.09,
  magicCrit: 0.09,
  hit: 10.5,
  magicHit: 10.5,
  dodge: 11,
  toughness: 5,
  maxHp: 228,
} as const;

/** Coins (เหรียญยุทธภพ): a win in the Swiss, then by place. */
export const TOURNEY_COINS = {
  swissWin: 10,
  entry: 5,
  /** By place in the knock-out: champion, runner-up, the two who lost the semi-finals. */
  place: [60, 40, 25, 25] as readonly number[],
} as const;

/** The weekly title (ฉายา) of the best four: by place. Thai; `{w}` is the week number. */
export const TOURNEY_TITLES: readonly string[] = [
  'ผู้ครองวิชาประจำสัปดาห์ที่ {w}',
  'รองผู้ครองวิชาสัปดาห์ที่ {w}',
  'ยอดฝีมือยุทธภพสัปดาห์ที่ {w}',
  'ยอดฝีมือยุทธภพสัปดาห์ที่ {w}',
];

/** Thai words the panel and the herald use. */
export const TOURNEY_TEXT = {
  name: 'ประลองยุทธภพ',
  herald: 'ผู้ประกาศยุทธภพ',
  heraldTitle: 'เปิดรับสมัครประลองยุทธภพ',
  hall: 'ทำเนียบผู้ครองวิชา',
  coins: 'เหรียญยุทธภพ',
  holder: 'ผู้ครองวิชา',
  bye: 'บาย',
  guest: 'ปรมาจารย์รับเชิญ',
} as const;

/** The unknown challengers who fill the field when the player is not in it (and the test field). */
export const TOURNEY_GUESTS: readonly string[] = ['ชายแปลกหน้าหน้ากากเหล็ก', 'นักพรตเร่ร่อนเหลียงฟู่', 'ผู้ท้าจากแดนไกลเฟิงอวี้', 'ศิษย์หนีสำนักอาหลัน'];

/** The herald stands beside the ring (metres, 256 m plains). */
export const HERALD_AT: readonly [number, number] = [100, 94];
